// AI assisted development
/**
 * Auth Guard Component
 * 
 * Ensures user is authenticated before rendering children.
 * Uses MSAL to trigger Azure AD login if not authenticated.
 * 
 * NOTE: In Vite dev server mode, authentication can be bypassed:
 * - implicitly when Azure AD config is missing
 * - explicitly by setting VITE_DISABLE_AUTH=true
 */

import { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import LoadingSpinner from './LoadingSpinner';
import { Box, Typography, Button } from '@mui/material';
import { signInRedirect } from '../services/auth';
import { AUTH_DISABLED } from '../config/auth';

interface RequireAuthProps {
  children: React.ReactNode;
}

export default function RequireAuth({ children }: RequireAuthProps) {
  const { accounts, inProgress } = useMsal();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const handleSignIn = async () => {
    if (isRedirecting) return;
    setIsRedirecting(true);
    try {
      await signInRedirect();
    } catch (error) {
      console.error('Sign in failed:', error);
    } finally {
      setIsRedirecting(false);
    }
  };

  // In dev-auth-disabled mode, skip authentication and render children immediately
  if (AUTH_DISABLED) {
    return <>{children}</>;
  }

  // Loading state - MSAL is still initializing or logging in
  if (inProgress !== 'none') {
    return <LoadingSpinner message="Signing in..." />;
  }

  // Not authenticated - show login prompt
  if (accounts.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2,
        }}
      >
        <Typography variant="h4" gutterBottom>
          🔒 Authentication Required
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          You need to be signed in to access the Monocle Quality Dashboard.
        </Typography>
        <Button
          variant="contained"
          size="large"
          disabled={isRedirecting}
          onClick={handleSignIn}
        >
          {isRedirecting ? 'Signing in...' : 'Sign In with Microsoft'}
        </Button>
      </Box>
    );
  }

  // Authenticated - render children
  return <>{children}</>;
}

